import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import RadarChart from './RadarChart';
import BrandLogo from './BrandLogo';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { generateRecommendation } from '../utils/scoring';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const { FiDownload, FiExternalLink, FiCpu, FiAward } = FiIcons;

const Results = ({ scores, userInfo, targetType, onRestart }) => {
  const reportRef = useRef();
  const [isGenerating, setIsGenerating] = useState(false);
  const recommendation = generateRecommendation(scores);

  const getScoreInterpretation = (score) => {
    if (score < 1.5) return { level: 'Optimal', color: 'text-brand-olive', bg: 'bg-brand-olive/10' };
    if (score < 2.5) return { level: 'Balanced', color: 'text-brand-golden', bg: 'bg-brand-golden/10' };
    if (score < 3.5) return { level: 'Attention Needed', color: 'text-brand-rose', bg: 'bg-brand-rose/10' };
    return { level: 'Action Required', color: 'text-brand-crimson', bg: 'bg-brand-crimson/10' };
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    const element = reportRef.current;
    
    try {
      // PDF Optimization: Ensure element is fully visible and styled for capture
      const canvas = await html2canvas(element, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#fffaf0',
        windowWidth: 1200, // Force a consistent width for capture
        onclone: (clonedDoc) => {
          // Additional styling for the cloned version to prevent cut-offs
          const clonedElement = clonedDoc.getElementById('pdf-report-container');
          if (clonedElement) {
            clonedElement.style.padding = '40px';
            clonedElement.style.height = 'auto';
            clonedElement.style.overflow = 'visible';
          }
        }
      });
      
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const finalWidth = imgWidth * ratio;
      const finalHeight = imgHeight * ratio;
      
      // Center the image
      const xOffset = (pdfWidth - finalWidth) / 2;
      
      pdf.addImage(imgData, 'PNG', xOffset, 10, finalWidth, finalHeight);
      pdf.save(`WeTheBillions-BrainMap-${userInfo.name || 'User'}.pdf`);
    } catch (err) {
      console.error('PDF Generation Failed:', err);
      alert("There was an issue generating your PDF. Please try again or take a screenshot.");
    } finally {
      setIsGenerating(false);
    }
  };

  const targetName = targetType === 'self' ? userInfo.name : userInfo.targetName;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen p-6 pb-20">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-20 h-20 bg-brand-crimson rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <SafeIcon icon={FiAward} className="text-brand-floral text-3xl" />
          </motion.div>
          <h1 className="text-3xl md:text-5xl font-bold text-brand-coffee font-luxe mb-2">Neural Mapping Complete</h1>
          <p className="text-lg text-brand-coffee/70 font-playfair">{targetName}, explore your unique brain architecture below.</p>
        </div>

        <div id="pdf-report-container" ref={reportRef} className="bg-brand-floral rounded-[2.5rem] shadow-2xl p-6 md:p-10 mb-12 border border-brand-dust relative">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <BrandLogo className="w-24 h-24" />
          </div>

          <div className="flex justify-between items-start mb-10 border-b border-brand-dust pb-6">
            <div>
              <h2 className="text-2xl font-bold text-brand-crimson font-luxe">Brain Map Report</h2>
              <p className="text-brand-coffee/60 uppercase tracking-widest text-[10px] font-bold">86 Billion Neurons • Personal Profile</p>
            </div>
            <div className="text-right text-sm">
              <p className="font-bold text-brand-coffee">{targetName}</p>
              <p className="text-brand-coffee/60">{new Date().toLocaleDateString()}</p>
            </div>
          </div>

          <div className="mb-12 bg-white/40 rounded-2xl p-4 border border-brand-dust/30">
            <RadarChart scores={scores} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            {Object.entries(scores).map(([key, score]) => {
              const interpretation = getScoreInterpretation(score);
              const regions = {
                cerebrumRoot: { name: 'Cerebrum-Root', desc: 'Baseline Attention' },
                cerebrumLeaf: { name: 'Cerebrum-Leaf', desc: 'Skill Execution' },
                cerebrumFlower: { name: 'Cerebrum-Flower', desc: 'Executive Logic' },
                limbicSystem: { name: 'Limbic System', desc: 'Emotional Core' },
                brainstem: { name: 'Brainstem', desc: 'Sensory Input' }
              };
              return (
                <div key={key} className="p-5 rounded-xl bg-white border border-brand-dust/50">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-bold text-brand-coffee text-sm">{regions[key].name}</h4>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${interpretation.bg} ${interpretation.color}`}>
                      {interpretation.level}
                    </span>
                  </div>
                  <div className="text-xl font-black text-brand-coffee">
                    {score.toFixed(1)} <span className="text-xs font-normal text-brand-coffee/30">/ 4.0</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-brand-crimson text-brand-floral p-6 rounded-2xl shadow-lg">
            <div className="flex items-center mb-3">
              <SafeIcon icon={FiCpu} className="text-xl mr-2 text-brand-golden" />
              <h3 className="font-bold font-luxe">Clinical Insight</h3>
            </div>
            <p className="text-sm leading-relaxed opacity-90">{recommendation}</p>
          </div>
        </div>

        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-brand-midnight text-brand-floral rounded-3xl p-8 text-center shadow-xl mb-12">
          <h3 className="text-2xl font-bold font-luxe mb-4">Unlock Your <span className="text-brand-golden">Full Map</span></h3>
          <p className="text-lg opacity-80 mb-8 max-w-xl mx-auto">Take the deep dive. Your snapshot shows areas that would benefit from our comprehensive 45-skill evaluation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wethebillions.co" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-brand-crimson hover:bg-brand-crimson/80 text-brand-floral rounded-xl font-bold transition-all flex items-center justify-center">
              Full Brain Map <SafeIcon icon={FiExternalLink} className="ml-2" />
            </a>
            <button onClick={generatePDF} disabled={isGenerating} className="px-8 py-4 bg-brand-floral text-brand-midnight rounded-xl font-bold transition-all flex items-center justify-center">
              {isGenerating ? 'Generating...' : 'Download PDF'} <SafeIcon icon={FiDownload} className="ml-2" />
            </button>
          </div>
        </motion.div>

        <div className="text-center">
          <button onClick={onRestart} className="text-brand-coffee/40 hover:text-brand-crimson font-bold uppercase tracking-widest text-xs transition-colors">
            Start New Neural Path
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default Results;